import { apiClient } from '../utils/api-client';
import type {
  MembershipOrganization,
  MembershipOrganizationPayload,
  MembershipOrganizationResponse,
  MembershipOrganizationsListResponse,
} from '../types/membership';

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw new Error(body.errors?.[0]?.detail || fallback);
}

class MembershipOrganizationsService {
  /**
   * Every body this gym can put on a card: the ones DivePeaks maintains plus its
   * own. Only the gym's own come back `editable`.
   */
  async list(gymSlug: string): Promise<MembershipOrganization[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/membership_organizations`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch membership organizations');
    }

    const data: MembershipOrganizationsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async create(
    gymSlug: string,
    payload: MembershipOrganizationPayload
  ): Promise<MembershipOrganization> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/membership_organizations`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the organization');
    }

    const data: MembershipOrganizationResponse = await response.json();
    return data.data.attributes;
  }

  async update(
    gymSlug: string,
    id: number,
    payload: MembershipOrganizationPayload
  ): Promise<MembershipOrganization> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/membership_organizations/${id}`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the organization');
    }

    const data: MembershipOrganizationResponse = await response.json();
    return data.data.attributes;
  }

  async remove(gymSlug: string, id: number): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/membership_organizations/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the organization');
    }
  }
}

export const membershipOrganizationsService = new MembershipOrganizationsService();
